// LIVE и живой батч — чистые функции (без DOM).
// Предикат LIVE: поле строки из config.match_center_sync.live_filter_field (сервер шлёт live_plausible), запас — поле
// кнопки шапки (action.field), затем is_live. Фильтр — на клиенте по уже пришедшему ответу: выключение LIVE возвращает
// полный список без нового запроса.
// Слияние батча (гид v5 «Списки: живой батч»): строки по id; ключи патча заменяют поля строки ЦЕЛИКОМ, включая null
// (минута закончившегося матча исчезает, winner_side очищается). Статус батча — объект: short → status,
// long → status_long, long_localized → status_long_localized. Строки, которых нет в батче, не трогаем.
import type { DayMatch, LiveBatchRow, MatchDayResponse } from '../../api/matchDay';

export const DEFAULT_LIVE_FIELD = 'is_live';

export function liveField(syncField: string | undefined, headerField: string | undefined): string {
  return syncField || headerField || DEFAULT_LIVE_FIELD;
}

/** Строка живая по полю фильтра; поля нет в строке — по is_live. */
export function isLiveRow(row: DayMatch, field: string = DEFAULT_LIVE_FIELD): boolean {
  const value = row[field];
  return typeof value === 'boolean' ? value : row.is_live === true;
}

/** Строка + патч батча. */
export function patchRow(row: DayMatch, patch: LiveBatchRow): DayMatch {
  const next: DayMatch = { ...row };
  // минута — своим ключом патча (null — «минуты нет»); ключа нет — из status.elapsed
  if ('minute' in patch) next.minute = patch.minute ?? null;
  else if (patch.status) next.minute = patch.status.elapsed ?? null;
  if ('minute_extra' in patch) next.minute_extra = patch.minute_extra ?? null;
  else if (patch.status) next.minute_extra = patch.status.extra ?? null;
  const short = patch.status?.short ?? patch.status_short;
  if (short) next.status = short;
  if (patch.status?.long !== undefined) next.status_long = patch.status.long;
  const localized = patch.status_long_localized ?? patch.status?.long_localized;
  if (localized !== undefined) next.status_long_localized = localized;
  if ('is_live' in patch) next.is_live = patch.is_live ?? false;
  if ('live_plausible' in patch) next.live_plausible = patch.live_plausible ?? false;
  if ('score' in patch) next.score = patch.score ?? null;
  return next;
}

/** День с применённым батчем. stale — батч старше ответа дня (день перечитан позже): не применяется, свежее то, что
 *  пришло последним. Ничего не поменялось — тот же объект (React не перерисует). */
export function mergeLiveBatch(day: MatchDayResponse, rows: LiveBatchRow[] | undefined, stale = false): MatchDayResponse {
  if (!rows?.length || stale) return day;
  const byId = new Map(rows.map((r) => [r.id, r]));
  let changed = false;
  const patch = (matches: DayMatch[]) => matches.map((m) => {
    const p = byId.get(m.id);
    if (!p) return m;
    changed = true;
    return patchRow(m, p);
  });
  const top = day.top_section?.leagues?.map((l) => ({ ...l, matches: patch(l.matches ?? []) }));
  const countries = day.countries?.map((c) => ({ ...c, leagues: c.leagues.map((l) => ({ ...l, matches: patch(l.matches ?? []) })) }));
  if (!changed) return day;
  return { ...day, top_section: day.top_section ? { ...day.top_section, leagues: top } : day.top_section, countries };
}

/** Все строки дня (блок топ-лиг и страны). */
export function allMatches(day: MatchDayResponse | undefined): DayMatch[] {
  if (!day) return [];
  return [
    ...(day.top_section?.leagues ?? []).flatMap((l) => l.matches ?? []),
    ...(day.countries ?? []).flatMap((c) => c.leagues.flatMap((l) => l.matches ?? [])),
  ];
}

/** Id живых строк — для батча by_ids. */
export function liveIds(day: MatchDayResponse | undefined, field?: string): number[] {
  return allMatches(day).filter((m) => isLiveRow(m, field) || m.is_live === true).map((m) => m.id);
}
